import { DateTime, Duration } from 'luxon';

import { type Either, type Maybe, left, right } from '@bedc/core';
import type { types } from '@bedc/types';

const getCurrentDateTime = (): string => new Date().toISOString();

export type DateTimeFormatOptions = {
	/** Whether to include the year (defaults to true). */
	showYear?: boolean;

	/** Whether to include the day of the week (defaults to false). */
	showWeekday?: boolean;

	/** Any manually overriden format type. */
	formatType?: Intl.DateTimeFormatOptions;

	/**
	 * The timezone to use for formatting (e.g. `'America/New_York'`). When set,
	 * it overrides any timezone carried by the date value itself.
	 */
	timeZone?: string | undefined;
};

/**
 * Formats a date value into a human-readable string.
 *
 * @param date Date object, ISO string or millisecond value.
 * @param options Formatting options.
 * @returns The human-readable string.
 */
const toDateString = (
	date: Date | number | string | types.time.Datetime,
	options?: DateTimeFormatOptions
) => {
	const formatType =
		options?.formatType ?? (options?.showWeekday ? DateTime.DATE_HUGE : DateTime.DATE_MED);

	const formatOptions: Intl.DateTimeFormatOptions = {
		...formatType,
	};

	if (options?.showYear === false) {
		formatOptions.year = undefined;
	}

	const dateTimezone = typeof date === 'object' && 'timezone' in date ? date.timezone : undefined;
	const timezone = options?.timeZone ?? dateTimezone;

	if (timezone) {
		formatOptions.timeZone = timezone;
	}

	const dateString = typeof date === 'object' && 'timestamp' in date ? date.timestamp : date;

	try {
		return getDateTime(dateString).toLocaleString(formatOptions);
	} catch (error) {
		console.error('Error formatting date string', dateString, formatOptions, error);
		return '';
	}
};

/**
 * Formats a date value into a human-readable time string.
 *
 * @param date Date object, ISO string or millisecond value.
 * @returns The human-readable string.
 */
const toTimeString = (date: Date | number | string | types.time.Time) => {
	const formatOptions: Intl.DateTimeFormatOptions = {
		...DateTime.TIME_SIMPLE,
	};

	const timeString = typeof date === 'object' && 'timestamp' in date ? date.timestamp : date;
	const timezone = typeof date === 'object' && 'timezone' in date ? date.timezone : undefined;

	if (timezone) {
		formatOptions.timeZone = timezone;
	}

	try {
		return getDateTime(timeString).toLocaleString(formatOptions);
	} catch (error) {
		console.error('Error formatting time string', timeString, formatOptions, error);
		return '';
	}
};

/**
 * Formats a date+time value into a human-readable string.
 *
 * @param date Date object, ISO string or millisecond value.
 * @param options Formatting options.
 * @returns The human-readable string.
 */
const toDateTimeString = (
	date?: Date | number | string | null | types.time.Datetime,
	options?: DateTimeFormatOptions
) => {
	if (!date) return '';

	const formatOptions: Intl.DateTimeFormatOptions = {
		...DateTime.DATETIME_MED,
	};

	if (options?.showYear === false) {
		formatOptions.year = undefined;
	}

	const datetimeString = typeof date === 'object' && 'timestamp' in date ? date.timestamp : date;
	const dateTimezone =
		typeof date === 'object' && 'timestamp' in date ? date.timezone : undefined;
	const timezone = options?.timeZone ?? dateTimezone;

	if (timezone) {
		formatOptions.timeZone = timezone;
	}

	try {
		return getDateTime(datetimeString).toLocaleString(formatOptions);
	} catch (error) {
		console.error('Error formatting datetime string', datetimeString, formatOptions, error);
		return '';
	}
};

/**
 * Converts a millisecond diff value into a duration object.
 *
 * @param diff Millisecond diff value.
 * @param formatStr How output should be formatted (or 'human' for friendly).
 * @param significantUnits Amount of significant units to display.
 * @returns The duration object.
 */
const toDiffStr = (
	diff: number,
	formatStr = 'hh:mm',
	significantUnits: 1 | 2 | 3 = 1,
	unitDisplay: 'long' | 'short' | 'narrow' = 'long'
) => {
	// Handle direct formatting.
	if (formatStr !== 'human') {
		return Duration.fromObject({ milliseconds: Math.abs(diff) }).toFormat(formatStr);
	}

	// Handle human-readable formatting.
	else {
		// Diff should be past/future indifferent and in seconds.
		const diffAbs = Math.abs(diff) / 1000;

		// Split the diff into their component units and round down.
		// Remove units that are no longer significant (0).
		const days = Math.floor(diffAbs / 3600 / 24) || undefined;
		let hours = Math.floor((diffAbs / 3600) % 24) || undefined;
		let minutes = Math.floor((diffAbs % 3600) / 60) || undefined;
		let seconds = Math.floor((diffAbs % 3600) % 60) || undefined;

		if (significantUnits === 1) {
			if (days || hours || minutes) seconds = undefined;
			if (days || hours) minutes = undefined;
			if (days) hours = undefined;
		} else if (significantUnits === 2) {
			if (days || hours || minutes) seconds = undefined;
			if (days) minutes = undefined;
		} else if (significantUnits === 3) {
			if (days) seconds = undefined;
		}

		// App doesn't update every second, so display special case for under
		// one minute.
		if ((!minutes && seconds) || diff < 1000) {
			return `<${normalizeDurationUnits(
				Duration.fromObject({ minutes: 1 }).toHuman({
					unitDisplay,
				}),
				unitDisplay
			)}`;
		}

		// Return as readable string.
		return normalizeDurationUnits(
			Duration.fromObject({
				days,
				hours,
				minutes,
				seconds,
			}).toHuman({ unitDisplay }),
			unitDisplay
		);
	}
};

const normalizeDurationUnits = (
	value: string,
	unitDisplay: 'long' | 'short' | 'narrow' = 'long'
) => {
	if (unitDisplay !== 'short') return value;
	return value
		.replace(/\bhrs\b/g, 'hr')
		.replace(/\bmins\b/g, 'min')
		.replace(/\bsecs\b/g, 'sec');
};

/**
 * Convienence method to construct a Luxon DateTime from variable sources.
 *
 * @param date Date object, ISO string or millisecond value.
 * @returns The Luxon DateTime object.
 */
const getDateTime = (date: Date | number | string) => {
	if (typeof date === 'number') {
		return DateTime.fromMillis(date);
	} else if (typeof date === 'string') {
		return DateTime.fromISO(date);
	} else {
		return DateTime.fromJSDate(date);
	}
};

const toDaysAndHours = (hours: number) => {
	if (hours < 0) {
		return { days: 0, hours: 0 };
	} else if (hours < 24) {
		return { days: 0, hours };
	}
	const days = Math.floor(hours / 24);
	const remainingHours = hours % 24;
	return { days, hours: remainingHours };
};

/**
 * Safely parse a value into a Date object.
 *
 * @param dateString The date string to parse.
 * @returns The parsed Date object or undefined if invalid.
 */
const safeParseDate = (
	dateInput?: string | null | undefined | number | Date | types.time.Datetime
): Maybe<Date> => {
	// Handle null
	if (dateInput === null) return undefined;

	// Handle undefined
	if (dateInput === undefined) return undefined;

	// Handle numbers (timestamps)
	if (typeof dateInput === 'number') {
		if (!isFinite(dateInput)) return undefined;
		const luxonDate = DateTime.fromMillis(dateInput);
		return luxonDate.isValid ? luxonDate.toJSDate() : undefined;
	}

	// Handle Date objects
	if (dateInput instanceof Date) {
		return isNaN(dateInput.getTime()) ? undefined : dateInput;
	}

	// Handle types.time.Datetime objects
	if (typeof dateInput === 'object' && 'timestamp' in dateInput && 'timezone' in dateInput) {
		const luxonDate = DateTime.fromISO(dateInput.timestamp, { zone: dateInput.timezone });
		return luxonDate.isValid ? luxonDate.toJSDate() : undefined;
	}

	// Handle strings
	if (typeof dateInput !== 'string' || (typeof dateInput === 'string' && !dateInput.length))
		return undefined;

	const luxonDate = DateTime.fromISO(dateInput);
	return luxonDate.isValid ? luxonDate.toJSDate() : undefined;
};

const isValidDate = (date: string | null | undefined | number | Date): boolean => {
	const parsedDate = safeParseDate(date);

	return parsedDate !== undefined;
};

/**
 * True if the date string is in the format YYYY-MM-DD
 */
const isValidDateString = (dateString: string): boolean => {
	if (!isValidDate(dateString)) {
		return false;
	}

	return dateString.match(/^\d{4}-\d{2}-\d{2}$/) !== null;
};

/**
 * True if the time string is in the format HH:MM:SS
 */
const isValidTimeString = (timeString: string): boolean => {
	if (!isValidDate(timeString)) {
		return false;
	}

	return timeString.match(/^\d{2}:\d{2}:\d{2}$/) !== null;
};

/**
 * True if the date time string is in the format YYYY-MM-DDTHH:MM:SS
 */
const isValidDateTimeString = (dateTimeString: string): boolean => {
	if (!isValidDate(dateTimeString)) {
		return false;
	}

	return (
		dateTimeString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3}Z|\+\d{2}:\d{2})?$/) !==
		null
	);
};

/**
 * True if the date time string is in the format YYYY-MM-DDTHH:MM:SSZ
 */
const isValidDateISOString = (dateTimeString: string): boolean => {
	if (!isValidDate(dateTimeString)) {
		return false;
	}

	return (
		dateTimeString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3}Z|\+\d{2}:\d{2})$/) !==
		null
	);
};

/**
 * Add a delta to a date
 *
 * @param epochMs - The epoch milliseconds to add the delta to
 * @param delta - The delta to add to the date
 * @returns The new date as a string in the format YYYY-MM-DDTHH:MM:SS.000Z
 */
const addDeltaToEpochMs = (epochMs: number, delta: types.time.Delta): Either<Error, number> => {
	if (Number.isNaN(epochMs)) {
		return left(new Error(`Invalid epoch milliseconds ${epochMs}`));
	}

	// Pin to UTC so calendar math (months/years) never crosses a DST boundary
	// in the machine's local zone. Without this the result depends on the
	// server timezone (UTC in prod/CI, but shifts ±1h on a DST-observing dev box).
	const dateTime = DateTime.fromMillis(epochMs, { zone: 'utc' });

	if (!dateTime.isValid) {
		return left(new Error(`Invalid date time ${dateTime.toISO()}`));
	}

	const newDateTime = dateTime.plus(delta);

	if (!newDateTime.isValid) {
		return left(new Error(`Invalid date time ${newDateTime.toISO()}`));
	}

	return right(newDateTime.toMillis());
};

/**
 * Add a delta to a date
 *
 * @param dateString - The date to add the delta to
 * @param delta - The delta to add to the date
 * @returns The new date as a string in the format YYYY-MM-DDTHH:MM:SS.000Z
 */
const addDeltaToDate = (dateString: string, delta: types.time.Delta): Either<Error, string> => {
	if (!isValidDate(dateString)) {
		return left(new Error(`Invalid date ${dateString}`));
	}

	if (!isValidDateTimeString(dateString) && !isValidDateISOString(dateString)) {
		return left(new Error(`Invalid date time string ${dateString}`));
	}

	if (!isValidDateISOString(dateString)) {
		dateString = `${dateString}.000Z`;
	}

	// Pin to UTC so calendar math (months/years) never crosses a DST boundary
	// in the machine's local zone (see addDeltaToEpochMs above).
	const dateTime = DateTime.fromISO(dateString, { zone: 'utc' });

	if (!dateTime.isValid) {
		return left(new Error(`Invalid date time ${dateTime.toISO()}`));
	}

	const newDateTime = dateTime.plus(delta);

	// bring the date time backt to UTC
	const utcDateTime = newDateTime.setZone('UTC');

	if (!utcDateTime.isValid) {
		return left(new Error(`Invalid date time ${utcDateTime.toISO()}`));
	}

	return right(
		utcDateTime.toISO({
			extendedZone: false,
			includeOffset: true,
			suppressSeconds: false,
			suppressMilliseconds: false,
		})
	);
};

/**
 * Formats a whole number of minutes as a compact duration like `3d 4h 3m`.
 * Leading zero-units are omitted (`4h 3m`, `12m`); a nullish or non-positive
 * value returns `fallback` (default `''`). Days are 24h units — when the input
 * is already weekend-excluded (e.g. lead time), `3d` means 3 working days.
 */
const formatMinutesAsDuration = (minutes?: number | null, fallback = ''): string => {
	if (minutes == null || !Number.isFinite(minutes) || minutes <= 0) return fallback;
	const total = Math.floor(minutes);
	const days = Math.floor(total / 1440);
	const hours = Math.floor((total % 1440) / 60);
	const mins = total % 60;
	const parts: string[] = [];
	if (days > 0) parts.push(`${days}d`);
	if (hours > 0) parts.push(`${hours}h`);
	if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
	return parts.join(' ');
};

/**
 * Resolve a date range preset into the dates it currently covers.
 *
 * * Deliberately timezone-free. The caller decides which calendar date counts as
 *   "today" and passes it as the reference, so this remains pure date arithmetic
 *   and the timezone question stays in one place.
 *
 * @param preset The preset to resolve.
 * @param reference The date the preset resolves relative to, as `YYYY-MM-DD`.
 * @returns The inclusive range the preset covers, as `YYYY-MM-DD` values.
 * @throws When the reference is not a valid date.
 */
const resolveDateRangePreset = (
	preset: types.time.dateRange.preset.Type,
	reference: string
): { from: string; to: string } => {
	// Pin to UTC so month and year arithmetic never shifts by a DST transition in
	// the machine's local zone.
	const referenceDate = DateTime.fromISO(reference, { zone: 'utc' });

	if (!referenceDate.isValid) {
		throw new Error(`Cannot resolve date range preset against invalid reference: ${reference}`);
	}

	switch (preset.type) {
		case 'today':
			return {
				from: referenceDate.toFormat('yyyy-MM-dd'),
				to: referenceDate.toFormat('yyyy-MM-dd'),
			};

		case 'yesterday': {
			const yesterday = referenceDate.minus({ days: 1 });
			return { from: yesterday.toFormat('yyyy-MM-dd'), to: yesterday.toFormat('yyyy-MM-dd') };
		}

		// Rolling day windows end on the reference date, so an N-day window starts N-1 days back.
		case 'last-7-days':
			return {
				from: referenceDate.minus({ days: 6 }).toFormat('yyyy-MM-dd'),
				to: referenceDate.toFormat('yyyy-MM-dd'),
			};

		case 'last-28-days':
			return {
				from: referenceDate.minus({ days: 27 }).toFormat('yyyy-MM-dd'),
				to: referenceDate.toFormat('yyyy-MM-dd'),
			};

		case 'last-30-days':
			return {
				from: referenceDate.minus({ days: 29 }).toFormat('yyyy-MM-dd'),
				to: referenceDate.toFormat('yyyy-MM-dd'),
			};

		case 'last-90-days':
			return {
				from: referenceDate.minus({ days: 89 }).toFormat('yyyy-MM-dd'),
				to: referenceDate.toFormat('yyyy-MM-dd'),
			};

		// A rolling twelve months likewise ends on the reference date.
		case 'last-12-months':
			return {
				from: referenceDate.minus({ months: 12 }).plus({ days: 1 }).toFormat('yyyy-MM-dd'),
				to: referenceDate.toFormat('yyyy-MM-dd'),
			};

		// The only preset covering a whole completed period rather than a period to date.
		case 'last-year': {
			const lastYear = referenceDate.minus({ years: 1 });
			return {
				from: lastYear.startOf('year').toFormat('yyyy-MM-dd'),
				to: lastYear.endOf('year').toFormat('yyyy-MM-dd'),
			};
		}

		// Calendar presets cover the complete period containing the reference date.
		case 'this-month':
			return {
				from: referenceDate.startOf('month').toFormat('yyyy-MM-dd'),
				to: referenceDate.endOf('month').toFormat('yyyy-MM-dd'),
			};

		case 'this-year':
			return {
				from: referenceDate.startOf('year').toFormat('yyyy-MM-dd'),
				to: referenceDate.endOf('year').toFormat('yyyy-MM-dd'),
			};
	}
};

/**
 * Resolve one semantic Search API date filter into an inclusive absolute range.
 *
 * @param filter The relative date filter to resolve.
 * @param now The instant used as the current time. Defaults to now.
 * @returns The equivalent absolute Search API date filter.
 */
const resolveSearchDateFilter = <
	FilterType extends Extract<
		NonNullable<types.api.generic.search.Request['filter']>[number],
		{ rangeType: types.api.generic.search.filter.RangeType }
	>,
>(
	filter: FilterType,
	now = DateTime.now()
): {
	field: FilterType['field'];
	rangeType: 'between';
	values: [string, string];
} => {
	if (filter.rangeType === 'between') {
		const convertValue = (value: string | number | null, end: boolean) => {
			if (value === null) return now.toUTC().toISO();
			if (typeof value === 'number') return now.plus({ milliseconds: value }).toUTC().toISO();

			const timezone = filter.timezone;
			const parsed = DateTime.fromISO(value, timezone ? { zone: timezone } : undefined);
			const isCalendarDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
			const date =
				timezone && isCalendarDate
					? end
						? parsed.endOf('day')
						: parsed.startOf('day')
					: parsed;
			return date.isValid ? date.toUTC().toISO() : value;
		};

		return {
			field: filter.field,
			rangeType: 'between' as const,
			values: [
				convertValue(filter.values[0], false),
				convertValue(filter.values[1], true),
			] as [string, string],
		};
	}

	const current = now.setZone(filter.timezone);
	const currentDayStart = current.startOf('day');
	const currentDayEnd = current.endOf('day');
	let from: DateTime;
	let to: DateTime;

	switch (filter.rangeType) {
		case 'current':
			from = current.startOf(filter.unit);
			to = current.endOf(filter.unit);
			break;

		case 'previous': {
			const previous = current.minus({ [`${filter.unit}s`]: 1 });
			from = previous.startOf(filter.unit);
			to = previous.endOf(filter.unit);
			break;
		}

		case 'last':
			from = currentDayStart.minus({ [`${filter.unit}s`]: filter.amount }).plus({ days: 1 });
			to = currentDayEnd;
			break;

		case 'next':
			from = currentDayStart;
			to = currentDayEnd.plus({ [`${filter.unit}s`]: filter.amount }).minus({ days: 1 });
			break;
	}

	return {
		field: filter.field,
		rangeType: 'between' as const,
		values: [from.toUTC().toISO(), to.toUTC().toISO()] as [string, string],
	};
};

export const timeUtils = {
	addDeltaToDate,
	addDeltaToEpochMs,
	formatMinutesAsDuration,
	getCurrentDateTime,
	getDateTime,
	isValidDate,
	isValidDateISOString,
	isValidDateString,
	isValidDateTimeString,
	isValidTimeString,
	resolveDateRangePreset,
	resolveSearchDateFilter,
	safeParseDate,
	toDateString,
	toDateTimeString,
	toDaysAndHours,
	toDiffStr,
	toTimeString,
};
